import { GiftIcon, ShoppingBagIcon } from "@xola/icons";
import clsx from "clsx";
import React, { useState } from "react";
import { Media, Product, productMap, ProductObject, ProductReference, PurchaseItemObject } from "types";
import { getFormMediaUrl } from "@/hooks/formMediaUrl";
import { getDefaultPhotoUrl, getPlaceholderImageUrl } from "@/models/Product";

// Additional sizes to be determined as they come
export const ImageSizes = {
    tiny: "w-6 h-6", // In places like PurchaseTileBreakdown
    small: "w-10 h-10",
    medium: "w-14 h-14", // the size that's present on the dashboard
    large: "w-16 h-16",
    huge: "w-28 h-28", // In places like MembershipTile.tsx
    giant: "w-32 h-32", // In places like CatalogItemImage, EventBanner
};

type ProductOrRef = Product | ProductReference;

export interface ProductImageProps {
    product: ProductOrRef;
    size?: keyof typeof ImageSizes;
    className?: string;
    classNames?: { image?: string; container?: string };
    style?: any;
    object?: PurchaseItemObject | ProductObject;
    src?: string;
    media?: Media;
}

export const ProductImage = ({
    product,
    object,
    size = "medium",
    media,
    className,
    classNames,
    src,
    ...rest
}: ProductImageProps) => {
    const imageClassName = clsx(
        "border object-fill rounded",
        product && "medias" in product && product.medias!.length > 0 ? "border-transparent" : "!border-gray-light",
        ImageSizes[size],
        classNames?.image ?? "",
    );

    return (
        <div
            className={clsx(
                "ui-product-image no-translate mr-3 flex-shrink-0 rounded border-[3px] border-transparent",
                className,
            )}
            {...rest}
        >
            {object === PurchaseItemObject.GIFT ? (
                <GiftImage className={clsx(imageClassName, "!border-yellow-darker !bg-yellow-lightish")} />
            ) : object === PurchaseItemObject.STORE_CREDIT ? (
                <StoreCreditImage className={clsx(imageClassName, "!border-blue-darker !bg-blue-lighter")} />
            ) : (
                <ExperienceImage
                    className={clsx(imageClassName, "no-translate")}
                    product={product}
                    size={size}
                    media={media}
                    src={src}
                />
            )}
        </div>
    );
};

/**
 * Maps UI size keys to Xola API size parameters.
 * Xola API doesn't support "tiny", "huge", or "giant" - they map to "small" or "large".
 * All sizes are returned with "_square" suffix as the app uses square images.
 * @returns {string} Size parameter like 'small_square', 'medium_square', or 'large_square'.
 */
export const getXolaImageSize = (size?: keyof typeof ImageSizes): string => {
    let xolaSize: string;

    switch (size) {
        case "tiny":
            xolaSize = "small";
            break;
        case "huge":
        case "giant":
            xolaSize = "large";
            break;
        default:
            xolaSize = size ?? "medium";
    }

    // All images have a square size as of X2-11603
    if (!xolaSize.endsWith("_square")) {
        xolaSize += "_square";
    }

    return xolaSize;
};

export interface ExperienceImageProps {
    product: Product | ProductReference;
    className: string;
    size: keyof typeof ImageSizes;
    src?: string;
    media?: Media;
}

const ExperienceImage = ({ product, size, className, src, media }: ExperienceImageProps) => {
    const xolaSize = getXolaImageSize(size);

    const productPath = productMap[product?.object ?? ProductObject.EXPERIENCE];
    const mediaPath = `/${productPath}/${product?.id}/medias/${media?.id}?size=${xolaSize}`;
    const mediaSource = getFormMediaUrl(mediaPath);
    const imageSource = src || (media && mediaSource) || getDefaultPhotoUrl(product, xolaSize);

    return (
        <Image
            data-product-id={product?.id}
            src={imageSource}
            alt={product?.name?.trim() ?? "Product Image"}
            className={className}
        />
    );
};

const GiftImage = ({ className }: { className: string }) => {
    return (
        <div className={clsx(className, "bg-white")}>
            <GiftIcon
                size="large"
                className="absolute left-1/2 top-1/2 -translate-y-2/4 -translate-x-2/4 text-yellow-darker"
            />
        </div>
    );
};

export const Image = ({ src, alt, className, ...rest }: { src: string; alt: string; className: string }) => {
    const [isError, setIsError] = useState(src.includes("placeholder"));
    const handleError = () => setIsError(true);

    const classNames = clsx(className, { "!border-gray-light": isError });
    const url = isError ? getPlaceholderImageUrl("medium") : src;

    return <img src={url} className={classNames} alt={alt} onError={handleError} {...rest} />;
};

const StoreCreditImage = ({ className }: { className: string }) => {
    return (
        <div className={clsx(className, "bg-white")}>
            <ShoppingBagIcon size="large" className="absolute left-1/2 top-1/2 -translate-y-2/4 -translate-x-2/4" />
        </div>
    );
};
